import { ListItem, ListItemIcon, ListItemText, Typography } from "@mui/material";

import BaseEntitySearchAutocomplete, {
  BaseEntitySearchAutocompleteInheritableProps,
} from "@/common/components/Entity/components/BaseEntitySearchAutocomplete";
import AppIcon from "@/common/components/Icons/AppIcon";
import { renderIf } from "@/common/helpers/render/renderIf";
import { StringHelper } from "@/common/helpers/string";
import { AutocompleteOptionType, BaseAutocompleteOption } from "@/common/ts/autocomplete";
import { apiClient } from "@/core/api/ApiClient";
import { DamageDetectionDto, DamageDetectionGetPaginatedDto } from "@/core/api/generated/v1";

import ContractInline from "../Contract/ContractInline";
import { getVehicleOptionTitleForOperation } from "../Vehicle/VehicleAutocomplete";
import VehicleInline from "../Vehicle/VehicleInline";

export type DamageDetectionAutocompleteOption = BaseAutocompleteOption<DamageDetectionDto>;

export const getDamageDetectionTitle = (data: DamageDetectionDto): string =>
  StringHelper.join(
    [
      data.vehicle ? getVehicleOptionTitleForOperation(data.vehicle) : data.localNumber,
      data.localNumber,
      data.externalNumber,
    ],
    ", ",
    {
      skipEmpty: true,
      uniqueOnly: true,
    },
  );

export const damageDetectionToOption = (
  data: DamageDetectionDto,
): DamageDetectionAutocompleteOption => ({
  id: data.id!,
  title: getDamageDetectionTitle(data),
  optionType: AutocompleteOptionType.Normal,
  data: data,
});

export interface DamageDetectionAutocompleteProps extends BaseEntitySearchAutocompleteInheritableProps<DamageDetectionDto> {
  searchFilters?: Partial<Omit<DamageDetectionGetPaginatedDto, "search" | "includeIds">>;
}

export default function DamageDetectionAutocomplete({
  searchFilters,

  isPreload = true,
  ...otherProps
}: DamageDetectionAutocompleteProps) {
  return (
    <BaseEntitySearchAutocomplete
      sx={{ minWidth: 200, flex: 1 }}
      entityToOption={damageDetectionToOption}
      isPreload={isPreload}
      request={{
        requestFunc: apiClient.damageDetectionsApi.apiV1DamageDetectionsGetPost,
        limit: 25,
        parameters: {
          nexusOpsTenant: EMPTY_TENANT_IDENTIFIER,
          damageDetectionGetPaginatedDto: {
            ...searchFilters,
          },
        },
        combineParameters: (params, newParams) => ({
          ...params,
          damageDetectionGetPaginatedDto: {
            ...params.damageDetectionGetPaginatedDto,
            ...newParams,
          },
        }),
        deps: [searchFilters],
      }}
      label='Damage detection'
      placeholder='Search...'
      renderOption={(props, option: DamageDetectionAutocompleteOption) => {
        return (
          <ListItem {...props}>
            <ListItemIcon>
              {renderIf()
                .if(option.optionType === AutocompleteOptionType.Normal)
                .then(<AppIcon of='damageDetection' />)
                .elseif(option.optionType === AutocompleteOptionType.DynamicCreateNew)
                .then(<AppIcon of='add' />)
                .render()}
            </ListItemIcon>
            <ListItemText
              primary={
                <>
                  <Typography
                    variant={
                      option.optionType === AutocompleteOptionType.DynamicCreateNew
                        ? "subtitle1"
                        : "body1"
                    }
                    component='div'
                  >
                    {option.title}
                  </Typography>
                  {option.data && (
                    <Typography component='div' variant='body2'>
                      {option?.data?.vehicle && (
                        <VehicleInline entity={option.data.vehicle} withIcon={false} />
                      )}
                      {option?.data?.contract && (
                        <ContractInline entity={option.data.contract} withIcon={false} />
                      )}
                    </Typography>
                  )}
                </>
              }
            />
          </ListItem>
        );
      }}
      {...otherProps}
    />
  );
}
